import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { BackButton } from '../components/BackButton';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { HelpTooltip } from '../components/HelpTooltip';

interface CreateStudentProfilePageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const CreateStudentProfilePage: React.FC<CreateStudentProfilePageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    filiere: '',
    annee: '',
    langue: 'francais' as const,
    email_contact: '',
    telephone_contact: '',
    matieres_interesse: [] as string[] // Matières dans lesquelles l'étudiant cherche de l'aide
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const filieres = [
    { value: 'IG', label: 'Informatique de Gestion (IG)' },
    { value: 'EE', label: 'Économie d\'Entreprise (EE)' },
    { value: 'IBM', label: 'International Business Management (IBM)' },
    { value: 'IS', label: 'Information Science (IS)' },
    { value: 'Autre', label: 'Autre' }
  ];

  const annees = [
    { value: '1', label: 'Année académique : 1' },
    { value: '2', label: 'Année académique : 2' },
    { value: '3', label: 'Année académique : 3' }
  ];

  // Matières par filière
  const subjectsByFiliere: Record<string, string[]> = {
    IBM: ['General Management', 'Accounting & Finance', 'Marketing & Communication', 'Leadership & Soft-skills', 'Economics', 'Business Law', 'Business Tools', 'Mathématiques'],
    IG: ['Programmation', 'Base de données', 'Réseaux & Sécurité', 'Gestion de projet', 'Analyse & Modélisation', 'Web Development', 'Mathématiques'],
    IS: ['Bibliothéconomie', 'Archivistique', 'Technologies Web', 'UX Design', 'Gestion de données', 'Recherche d\'information', 'Mathématiques'],
    EE: ['Management', 'Comptabilité', 'Finance', 'Marketing', 'Ressources Humaines', 'Economics', 'Droit des affaires', 'Mathématiques']
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubjectToggle = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      matieres_interesse: prev.matieres_interesse.includes(subject)
        ? prev.matieres_interesse.filter(s => s !== subject)
        : [...prev.matieres_interesse, subject]
    }));
  };

  const validateForm = () => {
    if (!formData.prenom.trim()) {
      showToast('Veuillez entrer votre prénom', 'error');
      return false;
    }

    if (!formData.nom.trim()) {
      showToast('Veuillez entrer votre nom', 'error');
      return false;
    }

    if (!formData.filiere) {
      showToast('Veuillez sélectionner votre filière', 'error');
      return false;
    }

    if (!formData.annee) {
      showToast('Veuillez sélectionner votre année d\'études', 'error');
      return false;
    }

    if (!formData.email_contact.trim()) {
      showToast('Veuillez entrer votre email de contact', 'error');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_contact)) {
      showToast('Veuillez entrer un email de contact valide', 'error');
      return false;
    }

    if (formData.matieres_interesse.length === 0) {
      showToast('Veuillez sélectionner au moins une matière dans laquelle vous cherchez de l\'aide', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) {
      return;
    }

    setIsSubmitting(true);

    try {
      const profileData: any = {
        user_id: user.id,
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        filiere: formData.filiere,
        matieres: formData.matieres_interesse.join(', '), // Matières d'intérêt pour les étudiants
        tarif: 0, // Pas de tarif pour les étudiants
        role: 'etudiant',
        annee: formData.annee,
        langue: formData.langue,
        email_contact: formData.email_contact.trim(),
        telephone_contact: formData.telephone_contact.trim()
      };

      // Création du profil étudiant

      const { error } = await api.profiles.create(profileData);

      if (error) {
        
        // Messages d'erreur plus spécifiques
        let errorMessage = 'Erreur lors de la création du profil';
        const errorMsg = (error as any).message || '';
        const errorCode = (error as any).code || '';
        
        if (errorMsg.includes('duplicate key') || errorCode === '23505') {
          errorMessage = 'Un profil existe déjà pour cet utilisateur. Redirection vers votre profil existant.';
          setTimeout(() => navigate('/profile'), 2000);
        } else if (errorMsg.includes('violates check constraint') || errorCode === '23514') {
          errorMessage = 'Données invalides. Vérifiez vos informations.';
        } else if (errorMsg.includes('null value') || errorCode === '23502') {
          errorMessage = 'Tous les champs obligatoires doivent être remplis';
        } else if (errorMsg.includes('permission denied') || errorCode === '42501') {
          errorMessage = 'Erreur de permissions. Veuillez vous reconnecter.';
        } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
        }
        
        showToast(errorMessage, 'error');
        return;
      }


      showToast('Profil étudiant créé avec succès ! Bienvenue dans la communauté HEG-Genève Tutor.', 'success');
      navigate('/profile');
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Breadcrumbs */}
        <div className="mb-8">
          <Breadcrumbs 
            items={[
              { label: 'Accueil', path: '/' },
              { label: 'Choix de rôle', path: '/role-selection' },
              { label: 'Profil étudiant' }
            ]} 
          />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-graduation-cap text-white text-2xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Créer votre profil étudiant-e
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Complétez votre profil pour pouvoir chercher et contacter des tuteur-trices dans vos matières d'intérêt
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Informations personnelles */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <i className="fas fa-user text-[#002c46] mr-3"></i>
                Informations personnelles
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="prenom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    required
                    value={formData.prenom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Votre prénom"
                  />
                </div>

                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Votre nom"
                  />
                </div>
              </div>
            </div>

            {/* Informations académiques */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <i className="fas fa-university text-[#002c46] mr-3"></i>
                Informations académiques
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="filiere" className="block text-sm font-semibold text-gray-700 mb-2">
                    Filière *
                  </label>
                  <select
                    id="filiere"
                    name="filiere"
                    required
                    value={formData.filiere}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choisissez votre filière</option>
                    {filieres.map(filiere => (
                      <option key={filiere.value} value={filiere.value}>
                        {filiere.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="annee" className="block text-sm font-semibold text-gray-700 mb-2">
                    Année d'études *
                  </label>
                  <select
                    id="annee"
                    name="annee"
                    required
                    value={formData.annee}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choisissez votre année</option>
                    {annees.map(annee => (
                      <option key={annee.value} value={annee.value}>
                        {annee.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Matières d'intérêt */}
            {formData.filiere && subjectsByFiliere[formData.filiere] && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-book text-[#002c46] mr-3"></i>
                  Matières dans lesquelles vous cherchez de l'aide *
                  <HelpTooltip content="Sélectionnez les matières pour lesquelles vous souhaitez trouver des tuteur-trices" />
                </h2>
                
                <div className="grid md:grid-cols-2 gap-3">
                  {subjectsByFiliere[formData.filiere].map(subject => (
                    <label key={subject} className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-blue-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.matieres_interesse.includes(subject)}
                        onChange={() => handleSubjectToggle(subject)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                      />
                      <span className="text-sm font-medium text-gray-700">{subject}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <i className="fas fa-envelope text-[#002c46] mr-3"></i>
                Informations de contact
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email_contact" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email de contact *
                    <HelpTooltip content="Email que les tuteur-trices pourront voir pour vous contacter" />
                  </label>
                  <input
                    type="email"
                    id="email_contact"
                    name="email_contact"
                    required
                    value={formData.email_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div>
                  <label htmlFor="telephone_contact" className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone de contact
                    <HelpTooltip content="Numéro de téléphone optionnel pour faciliter le contact" />
                  </label>
                  <input
                    type="tel"
                    id="telephone_contact"
                    name="telephone_contact"
                    value={formData.telephone_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>
              </div>
            </div>

            {/* Langue */}
            <div>
              <label htmlFor="langue" className="block text-sm font-semibold text-gray-700 mb-2">
                Langue de communication préférée
              </label>
              <select
                id="langue"
                name="langue"
                value={formData.langue}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="francais">Français</option>
                <option value="anglais">Anglais</option>
              </select>
            </div>

            {/* Boutons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <BackButton className="flex-1" />
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Création en cours...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check mr-2"></i>
                    Créer mon profil étudiant
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
